import { jsx as _jsx } from "react/jsx-runtime";
/*
 * FlowForm - Keyboard Navigation System
 *
 * A FlowForm is a container that provides automated keyboard navigation and focus management
 * for widgets. It enables efficient keyboard-only interaction with forms and UI elements.
 * FlowForm navigation only works for '.flowFocussable' elements that are descendants of a
 * rendered <FlowForm> (or '.flowFormModal') container. Adding 'flowFocussable' on its own is
 * not enough; if a widget such as FeedTile is rendered outside a FlowForm, Arrow Up/Down will
 * not include it in the navigation order.
 *
 * USING FLOWFORM IN YOUR WIDGETS:
 *
 * 1. Render the relevant section inside <FlowForm> (or use '.flowFormModal' for modals):
 *    <FlowForm>
 *      ...
 *    </FlowForm>
 *
 * 2. Add the 'flowFocussable' class to make an element keyboard-focusable:
 *    <button className="flowFocussable" tabIndex={0} ...>
 *
 * 3. For elements that should only be accessible via sideways navigation (left/right arrows),
 *    add both 'flowFocussable' and 'sidewayFocus' classes:
 *    <button className="flowFocussable sidewayFocus" tabIndex={0} ...>
 *
 * 4. For 2D spatial navigation (grids, layouts), add both 'flowFocussable' and 'flow2d' classes:
 *    <button className="flowFocussable flow2d" tabIndex={0} ...>
 *    Then use get2DKeyHandler() for automatic arrow key navigation in all four directions.
 *
 * 5. Use getKeyHandler() to handle keyboard events with standard navigation behaviors:
 *    const handleKeyDown = getKeyHandler({
 *        enterBehaviour: 'action',      // What Enter does: 'next' | 'action' | 'ignore'
 *        spaceBehaviour: 'action',      // What Space does: 'action' | 'ignore' | 'atBeginning'
 *        sidewaysBehaviour: 'both',     // Arrow key navigation: 'off' | 'left' | 'right' | 'both' | 'leftAtBeginning' | 'rightAtEnd' | 'bothText' | '2d'
 *        onAction: () => doSomething()  // Called when action key is pressed
 *    });
 *
 * 6. Apply the handler to your element:
 *    <button onKeyDown={handleKeyDown} className="flowFocussable" tabIndex={0} ...>
 *
 * EXAMPLES FROM EXISTING WIDGETS:
 *
 * Simple clickable text (FocusText.tsx):
 *   const handleKeyDown = getKeyHandler({
 *       enterBehaviour: 'action',
 *       spaceBehaviour: 'action',
 *       sidewaysBehaviour: 'both',
 *       onAction: () => onClick?.()
 *   });
 *   <span className="flowFocussable" tabIndex={0} onKeyDown={handleKeyDown}>...</span>
 *
 * Checkbox with action (Checkbox.tsx):
 *   const handleKeyDown = getKeyHandler({
 *       enterBehaviour: 'action',
 *       spaceBehaviour: 'action',
 *       sidewaysBehaviour: 'both',
 *       onAction: () => onToggle?.(!checked)
 *   });
 *   <button className="flowFocussable" tabIndex={0} onKeyDown={handleKeyDown}>...</button>
 *
 * Tab buttons - first tab is normal flow, others are sideways (TabButtons.tsx):
 *   <TextButton
 *       focussable={index == 0 ? 'flow' : 'sideway'}
 *       ...
 *   />
 *
 * 2D Grid Navigation (using convenience function):
 *   const handleKeyDown = get2DKeyHandler(() => onClick?.());
 *   <button className="flowFocussable flow2d" tabIndex={0} onKeyDown={handleKeyDown}>
 *       Grid Item
 *   </button>
 *
 * 2D Grid Navigation (explicit configuration):
 *   const handleKeyDown = getKeyHandler({
 *       enterBehaviour: 'action',
 *       spaceBehaviour: 'action',
 *       sidewaysBehaviour: '2d',
 *       onAction: () => onClick?.()
 *   });
 *   <button className="flowFocussable flow2d" tabIndex={0} onKeyDown={handleKeyDown}>
 *       Grid Item
 *   </button>
 *
 * FOCUS CLASSES:
 * - 'flowFocussable': Element can receive focus via up/down arrow keys when inside a FlowForm
 * - 'flowFocussable sidewayFocus': Element can only receive focus via left/right arrow keys
 * - 'flowFocussable flow2d': Element can receive focus via 2D spatial navigation (north/south/east/west)
 *
 * NAVIGATION KEYS:
 * - Arrow Up/Down: Navigate between flowFocussable elements (excluding sidewayFocus)
 * - Arrow Left/Right: Navigate between sidewayFocus elements and within text inputs
 * - Enter: Move to next element (enterBehaviour: 'next') or trigger action (enterBehaviour: 'action')
 * - Space: Trigger action (spaceBehaviour: 'action')
 * - Tab: Standard browser behavior (not handled by FlowForm)
 *
 * 2D NAVIGATION (flow2d):
 * - Use get2DKeyHandler(onAction) convenience function for automatic spatial navigation
 * - Or use sidewaysBehaviour: '2d' in getKeyHandler() to enable spatial navigation with arrow keys
 * - Or call focusElement2d(element, 'north'|'south'|'east'|'west') directly for manual control
 * - East/West: Finds closest element to the right/left with vertical overlap
 * - North/South: Finds closest element above/below with horizontal overlap
 * - Falls back to regular focusElement() for north/south if no 2d element found
 * - East/West does nothing if no 2d element found
 */
import { createContext, useContext, useEffect, useId } from "react";
import { getKeyName, registerAnyKeyHandler, registerKeyHandler, unregisterAnyKeyHandler, unregisterKeyHandler } from "./GlobalKeyHandler";
export const FlowFormLeaveContext = createContext(undefined);
const leaveFormHandlers = new Map();
function tryLeaveForm(flowFormParent, dir, from) {
    var _a;
    const formId = (_a = flowFormParent.dataset) === null || _a === void 0 ? void 0 : _a.flowformId;
    const handler = formId ? leaveFormHandlers.get(formId) : undefined;
    if (!handler)
        return false;
    const target = handler(dir, from);
    if (target instanceof HTMLElement) {
        target.focus();
        return true;
    }
    return false;
}
export function setFocusMode(mode) {
    const flowForms = document.querySelectorAll('.flowForm, .flowFormModal');
    flowForms.forEach((flowForm) => {
        if (mode == 'hidden') {
            flowForm.classList.add('hiddenFocus');
            flowForm.classList.remove('visibleFocus');
        }
        else {
            flowForm.classList.remove('hiddenFocus');
            flowForm.classList.add('visibleFocus');
        }
    });
}
function getTextarea(ev) {
    const target = ev.target;
    if (target.tagName == "TEXTAREA")
        return target;
    if (target.tagName == "INPUT")
        return target;
    return undefined;
}
function isAtBeginning(ev) {
    const ta = getTextarea(ev);
    if (!ta)
        return false;
    return ta.selectionStart == 0;
}
function isAtEnd(ev) {
    var _a;
    const ta = getTextarea(ev);
    if (!ta)
        return false;
    return ta.selectionEnd == ((_a = ta.value) === null || _a === void 0 ? void 0 : _a.length);
}
function isEmpty(ev) {
    var _a;
    const ta = getTextarea(ev);
    return 0 == ((_a = ta === null || ta === void 0 ? void 0 : ta.value) === null || _a === void 0 ? void 0 : _a.length);
}
function hasSelection(ev) {
    const ta = getTextarea(ev);
    return (ta === null || ta === void 0 ? void 0 : ta.selectionStart) != (ta === null || ta === void 0 ? void 0 : ta.selectionEnd);
}
function isVisible(element) {
    const style = getComputedStyle(element);
    if (style.display == 'none' || style.visibility == 'hidden' || style.opacity == '0')
        return false;
    if (element.closest('.closed'))
        return false;
    return true;
}
export function focusFirstInForm(form) {
    const firstNonSide = form.querySelector('.flowFocussable:not(.sidewayFocus):not(.touchOnly)');
    if (firstNonSide) {
        firstNonSide.focus();
        return true;
    }
    const all = form.querySelectorAll('.flowFocussable');
    const first = Array.from(all).find((e) => e instanceof HTMLElement && isVisible(e));
    first === null || first === void 0 ? void 0 : first.focus();
    return !!first;
}
export function focusFirstElement() {
    var _a;
    const form = (_a = document.querySelector('.flowForm.mainForm')) !== null && _a !== void 0 ? _a : document.querySelector('.flowForm');
    return form ? focusFirstInForm(form) : false;
}
export function focusMenu() {
    const menuButton = document.querySelector('button.menuOnMobile, button.menu');
    if (menuButton && isVisible(menuButton)) {
        menuButton.focus();
        return true;
    }
    const first = document.querySelector('.menuBar .flowFocussable.menuItem');
    if (first && isVisible(first)) {
        first.focus();
    }
    return !!first;
}
export function focusElement(startElement, direction, sidewayFocus) {
    var _a, _b, _c, _d;
    const flowFormParent = (_a = startElement.closest('.flowForm')) !== null && _a !== void 0 ? _a : startElement.closest('.flowFormModal');
    if (!flowFormParent) {
        return direction == 1 ? focusFirstElement() : focusMenu();
    }
    if (direction == 1 && sidewayFocus) {
        // move inside a flowFocussable
        const childFocussable = startElement.querySelector('.flowFocussable');
        if (childFocussable) {
            childFocussable.focus();
            return true;
        }
    }
    const elements = Array.from(flowFormParent.querySelectorAll('.flowFocussable'));
    while (elements[0] && !isVisible(elements[0])) // remove invisible elements from beginning
        elements.shift();
    const index = Array.from(elements).indexOf(startElement);
    // if arrow up/left and at beginning, try leave form or move to previous form
    if (index == 0 && direction == -1) {
        if (sidewayFocus) {
            if (tryLeaveForm(flowFormParent, 'left', startElement))
                return true;
            return focusMenu();
        }
        else {
            if (tryLeaveForm(flowFormParent, 'up', startElement))
                return true;
            const elements = document.querySelectorAll('.flowForm');
            const formIndex = Array.from(elements).indexOf(startElement.closest('.flowForm'));
            if (formIndex > 0) {
                const nextFlowForm = elements[formIndex - 1];
                focusFirstInForm(nextFlowForm);
                return true;
            }
        }
    }
    // Right: only focus the next VISIBLE element, and only if it's sideway. Invisible
    // elements are skipped rather than blocking the move — e.g. a DropDownSelector's
    // hidden touch-device <select> sits between two visible widgets in the DOM.
    if (sidewayFocus && direction == 1) {
        let i = index + 1;
        while (elements[i] && !isVisible(elements[i]))
            i++;
        const next = elements[i];
        if (next && next.classList.contains('sidewayFocus')) {
            next.focus();
            return true;
        }
        // Left from a sideway element: scan backward for any element
    }
    else if (sidewayFocus && startElement.classList.contains('sidewayFocus')) {
        let i = index + direction;
        while (i >= 0 && elements[i]) {
            const e = elements[i];
            if (isVisible(e)) {
                e.focus();
                return true;
            }
            i += direction;
        }
        // Left from a non-sideway element: do nothing (fall through to boundary handling)
        // Up/Down: scan for non-sideway elements
    }
    else if (!sidewayFocus) {
        let i = index + direction;
        while (i >= 0 && elements[i]) {
            const e = elements[i];
            if (isVisible(e) && !e.classList.contains('sidewayFocus')) {
                e.focus();
                return true;
            }
            i += direction;
        }
    }
    // move out of an inner flowFocussable (start from parent to avoid matching self)
    if (sidewayFocus && ((_c = (_b = startElement.parentElement) === null || _b === void 0 ? void 0 : _b.closest('.flowFocussable, .flowForm')) === null || _c === void 0 ? void 0 : _c.classList.contains('flowFocussable'))) {
        const parentFocussable = startElement.parentElement.closest('.flowFocussable');
        parentFocussable.focus();
        return true;
    }
    // if arrow down/right and at end, try leave form or move to next flowForm
    if (direction == 1) {
        const dir = sidewayFocus ? 'right' : 'down';
        if (tryLeaveForm(flowFormParent, dir, startElement))
            return true;
        if (!sidewayFocus) {
            const elements = document.querySelectorAll('.flowForm');
            const formIndex = Array.from(elements).indexOf(startElement.closest('.flowForm'));
            if (formIndex >= 0 && formIndex < elements.length - 1) {
                const nextFlowForm = elements[formIndex + 1];
                (_d = nextFlowForm.querySelector('.flowFocussable:not(.sidewayFocus), .flowFocussable')) === null || _d === void 0 ? void 0 : _d.focus();
                return true;
            }
        }
    }
    // if arrow left and no element found in scan, try leave form
    if (direction == -1 && sidewayFocus) {
        if (tryLeaveForm(flowFormParent, 'left', startElement))
            return true;
    }
    return false;
}
/**
 * Focus navigation in 2D space for elements with 'flow2d' class
 * Finds the closest focusable element in the specified direction based on spatial position
 */
export function focusElement2d(startElement, direction) {
    var _a;
    const flowFormParent = (_a = startElement.closest('.flowForm')) !== null && _a !== void 0 ? _a : startElement.closest('.flowFormModal');
    if (!flowFormParent) {
        return false;
    }
    // Find all flow2d elements in the form
    const elements = Array.from(flowFormParent.querySelectorAll('.flowFocussable.flow2d'))
        .filter(el => isVisible(el) && el !== startElement);
    if (elements.length === 0) {
        // If no 2d elements found and direction is north/south, fall back to regular focus
        if (direction === 'north') {
            return focusElement(startElement, -1, false);
        }
        else if (direction === 'south') {
            return focusElement(startElement, 1, false);
        }
        return false;
    }
    const startRect = startElement.getBoundingClientRect();
    let bestElement = null;
    let bestDistanceScore = Infinity;
    let bestLateral = Infinity;
    for (const element of elements) {
        const rect = element.getBoundingClientRect();
        let isInDirection = false;
        let directionDistance = 0;
        let lateral = 0;
        let distance = 0;
        switch (direction) {
            case 'east':
                // Element must be to the right and have vertical overlap
                if (rect.left > startRect.right) {
                    isInDirection = true;
                    directionDistance = rect.left - startRect.right;
                    lateral = Math.max(0, rect.top - startRect.bottom, startRect.top - rect.bottom);
                    distance = Math.sqrt(directionDistance ** 2 + lateral ** 2);
                }
                break;
            case 'west':
                // Element must be to the left and have vertical overlap
                if (rect.right < startRect.left) {
                    isInDirection = true;
                    directionDistance = startRect.left - rect.right;
                    lateral = Math.max(0, rect.top - startRect.bottom, startRect.top - rect.bottom);
                    distance = Math.sqrt(directionDistance ** 2 + lateral ** 2);
                }
                break;
            case 'south':
                // Element must be below (at least one pixel under startElement)
                if (rect.top >= startRect.bottom) {
                    isInDirection = true;
                    directionDistance = rect.top - startRect.bottom;
                    lateral = Math.max(0, rect.left - startRect.right, startRect.left - rect.right);
                    distance = Math.sqrt(directionDistance ** 2 + lateral ** 2);
                }
                break;
            case 'north':
                // Element must be above (at least one pixel above startElement)
                if (rect.bottom <= startRect.top) {
                    isInDirection = true;
                    directionDistance = startRect.top - rect.bottom;
                    lateral = Math.max(0, rect.left - startRect.right, startRect.left - rect.right);
                    distance = Math.sqrt(directionDistance ** 2 + lateral ** 2);
                }
                break;
        }
        if (isInDirection) {
            const distanceScore = distance * 2 < directionDistance ? distance : directionDistance;
            // In a grid, every element of the adjacent row/column has the same directionDistance,
            // so the score alone ties across the whole row; the lateral offset breaks the tie in
            // favor of the element straight ahead of startElement.
            if (distanceScore < bestDistanceScore || (distanceScore == bestDistanceScore && lateral < bestLateral)) {
                bestElement = element;
                bestDistanceScore = distanceScore;
                bestLateral = lateral;
            }
        }
    }
    if (bestElement) {
        bestElement.focus();
        return true;
    }
    // No element found in the specified direction
    // For north/south, fall back to finding non-2D elements (exit the 2D grid)
    if (direction === 'north' || direction === 'south') {
        const focusDirection = direction === 'north' ? -1 : 1;
        // Get all flowFocussable elements that are NOT flow2d
        const non2dElements = Array.from(flowFormParent.querySelectorAll('.flowFocussable:not(.flow2d)'))
            .filter(el => isVisible(el) && !el.classList.contains('sidewayFocus'));
        const startIndex = non2dElements.indexOf(startElement);
        // If startElement is not in the non-2D list, find the closest non-2D element
        if (startIndex === -1) {
            // Find the closest non-2D element in the specified direction
            const allElements = Array.from(flowFormParent.querySelectorAll('.flowFocussable'));
            const currentIndex = allElements.indexOf(startElement);
            if (focusDirection === 1) {
                // Moving down/forward - find first non-2D element after this one in DOM order
                for (let i = currentIndex + 1; i < allElements.length; i++) {
                    const el = allElements[i];
                    if (isVisible(el) && !el.classList.contains('flow2d') && !el.classList.contains('sidewayFocus')) {
                        el.focus();
                        return true;
                    }
                }
            }
            else {
                // Moving up/backward - find first non-2D element before this one in DOM order
                for (let i = currentIndex - 1; i >= 0; i--) {
                    const el = allElements[i];
                    if (isVisible(el) && !el.classList.contains('flow2d') && !el.classList.contains('sidewayFocus')) {
                        el.focus();
                        return true;
                    }
                }
            }
        }
        else {
            return focusElement(startElement, focusDirection, false);
        }
    }
    // No element found in the grid: try to leave the form, mirroring the non-2d focusElement
    // boundary behaviour (e.g. up into a page-header menu, left into the sidebar). No-op if the
    // form has no leave handler for that direction.
    if (direction === 'north' || direction === 'south') {
        if (tryLeaveForm(flowFormParent, direction === 'north' ? 'up' : 'down', startElement))
            return true;
        // Still nothing: move to the previous/next flowForm (e.g. from the top card row up into a
        // page-header form), exactly like focusElement does at the form boundary.
        const forms = Array.from(document.querySelectorAll('.flowForm'));
        const formIndex = forms.indexOf(flowFormParent);
        if (direction === 'north' && formIndex > 0) {
            if (focusFirstInForm(forms[formIndex - 1]))
                return true;
        }
        if (direction === 'south' && formIndex >= 0 && formIndex < forms.length - 1) {
            const el = forms[formIndex + 1].querySelector('.flowFocussable:not(.sidewayFocus), .flowFocussable');
            if (el) {
                el.focus();
                return true;
            }
        }
    }
    if (direction === 'east' || direction === 'west') {
        if (tryLeaveForm(flowFormParent, direction === 'east' ? 'right' : 'left', startElement))
            return true;
    }
    return false;
}
function focusElementByEvent(ev, direction, sidewayFocus) {
    const element = ev.target;
    if (focusElement(element, direction, sidewayFocus)) {
        ev.preventDefault();
        ev.stopPropagation();
        return true;
    }
    return false;
}
function focusNext(ev, allowSideway = false) {
    return focusElementByEvent(ev, 1, allowSideway);
}
function focusPrev(ev, allowSideway = false) {
    return focusElementByEvent(ev, -1, allowSideway);
}
function handleKeyDown(ev, { multiLine = false, enterBehaviour = 'next', spaceBehaviour = 'ignore', sidewaysBehaviour = 'off', upDownBehaviour = 'both', onAction, onDelete, overrideHandlers, defaultHandler, upElement, downElement } = {}) {
    setFocusMode('visible');
    const key = getKeyName(ev);
    if (overrideHandlers === null || overrideHandlers === void 0 ? void 0 : overrideHandlers[key]) {
        if (overrideHandlers[key](ev.target)) {
            ev.preventDefault();
            ev.stopPropagation();
            return true;
        }
    }
    if (defaultHandler && defaultHandler(key)) {
        ev.preventDefault();
        ev.stopPropagation();
        return true;
    }
    const evaluatedEnterBehaviour = (key == 'Enter' && typeof enterBehaviour == 'function') ? enterBehaviour(ev.target) : enterBehaviour;
    if (onAction && (((key == 'Enter' && evaluatedEnterBehaviour == 'action') ||
        (key == 'Space' && spaceBehaviour == 'action') ||
        (key == 'Space' && spaceBehaviour == 'atBeginning' && isAtBeginning(ev))))) {
        ev.preventDefault();
        ev.stopPropagation();
        onAction(ev.target);
        return true;
    }
    if (onDelete && (key == 'Delete' || key == 'Backspace')) {
        ev.preventDefault();
        ev.stopPropagation();
        onDelete(ev.target);
        return true;
    }
    const fixedFocus = (upElement && key == 'ArrowUp') ? upElement :
        (downElement && key == 'ArrowDown') ? downElement : undefined;
    if (fixedFocus) {
        const target = document.querySelector(fixedFocus);
        if (target) {
            target.focus();
            ev.preventDefault();
            ev.stopPropagation();
            return true;
        }
    }
    // Handle 2D navigation mode
    if (sidewaysBehaviour == '2d') {
        const element = ev.target;
        // Up/down consume the key even when no target was found: falling through to the linear
        // focusPrev/focusNext below would move focus SIDEWAYS to a flow2d sibling (linear order
        // doesn't skip them), which reads as "up moved me right". focusElement2d already exhausts
        // every legitimate exit (non-2d elements, leave handler, neighboring forms).
        if (key == 'ArrowUp' || key == 'ArrowDown') {
            focusElement2d(element, key == 'ArrowUp' ? 'north' : 'south');
            ev.preventDefault();
            ev.stopPropagation();
            return true;
        }
        else if (key == 'ArrowLeft' && !hasSelection(ev)) {
            if (focusElement2d(element, 'west')) {
                ev.preventDefault();
                ev.stopPropagation();
                return true;
            }
        }
        else if (key == 'ArrowRight' && !hasSelection(ev)) {
            if (focusElement2d(element, 'east')) {
                ev.preventDefault();
                ev.stopPropagation();
                return true;
            }
        }
    }
    const effectiveUpDownBehaviour = typeof upDownBehaviour == 'function' ? upDownBehaviour(ev.target) : upDownBehaviour;
    if ((key == 'Enter' && evaluatedEnterBehaviour == 'next' && (!multiLine || isEmpty(ev))) ||
        (key == 'ArrowDown' && !multiLine) ||
        (key == 'ArrowDown' && multiLine && isAtEnd(ev) && !hasSelection(ev))) {
        if (effectiveUpDownBehaviour != 'up')
            return focusNext(ev);
        else {
            ev.preventDefault();
            return true;
        }
    }
    if ((key == 'ArrowUp' && !multiLine) ||
        (key == 'ArrowUp' && multiLine && isAtBeginning(ev) && !hasSelection(ev))) {
        if (effectiveUpDownBehaviour != 'down')
            return focusPrev(ev);
        else {
            ev.preventDefault();
            return true;
        }
    }
    if (sidewaysBehaviour != 'off' && sidewaysBehaviour != '2d') {
        if (key == 'ArrowLeft' && !hasSelection(ev) && (sidewaysBehaviour == 'left' || sidewaysBehaviour == 'both' || ((sidewaysBehaviour == 'leftAtBeginning' || sidewaysBehaviour == 'bothText') && isAtBeginning(ev)))) {
            ev.preventDefault();
            ev.stopPropagation();
            return focusPrev(ev, true);
        }
        if (key == 'ArrowRight' && !hasSelection(ev) && (sidewaysBehaviour == 'right' || sidewaysBehaviour == 'both' || ((sidewaysBehaviour == 'rightAtEnd' || sidewaysBehaviour == 'bothText') && isAtEnd(ev)))) {
            ev.preventDefault();
            ev.stopPropagation();
            return focusNext(ev, true);
        }
    }
    return false;
}
export function getKeyHandler(keyOptions) {
    if (typeof keyOptions == 'function')
        return (ev) => handleKeyDown(ev, {
            enterBehaviour: 'action',
            spaceBehaviour: 'action',
            sidewaysBehaviour: 'both',
            onAction: keyOptions
        });
    else if (keyOptions)
        return (ev) => handleKeyDown(ev, keyOptions);
    else
        return (ev) => handleKeyDown(ev, {
            enterBehaviour: 'next',
            spaceBehaviour: 'ignore',
            sidewaysBehaviour: 'both',
        });
}
/**
 * Convenience function for 2D spatial navigation
 * Preconfigured with sidewaysBehaviour: '2d' for grid/spatial layouts
 * Enter and Space trigger the action handler
 */
export function get2DKeyHandler(onAction) {
    return (ev) => handleKeyDown(ev, {
        enterBehaviour: 'action',
        spaceBehaviour: 'action',
        sidewaysBehaviour: '2d',
        onAction: onAction
    });
}
function focusFirstVisibleElement(containerSelector) {
    const elements = document.querySelectorAll(`${containerSelector} .flowFocussable:not(.sidewayFocus):not(.touchOnly)`);
    let firstPartiallyVisible;
    for (const el of elements) {
        if (!isVisible(el))
            continue;
        const rect = el.getBoundingClientRect();
        if (rect.top >= 0 && rect.bottom <= window.innerHeight) {
            el.focus();
            return true;
        }
        if (!firstPartiallyVisible && rect.bottom > 0 && rect.top < window.innerHeight) {
            firstPartiallyVisible = el;
        }
    }
    if (firstPartiallyVisible) {
        firstPartiallyVisible.focus();
        return true;
    }
    const form = document.querySelector(containerSelector);
    if (form) {
        return focusFirstInForm(form);
    }
    return false;
}
// Pages using FlowForm should call this hook to register the global key handler.
// Pass a CSS selector to screenBased to focus the first fully visible element within that container instead of the first element in the document.
export function useFlowFormGlobal(screenBased) {
    useEffect(() => {
        registerKeyHandler('flowFormGlobal', {
            'ArrowDown': () => {
                // Move focus into the form when nothing in a form is focused yet —
                // i.e. focus is on <body> or on an element outside any flow form
                // (e.g. a page header that is not itself a flowForm).
                const ae = document.activeElement;
                if (!ae || ae.tagName == 'BODY' || !ae.closest('.flowForm, .flowFormModal')) {
                    return screenBased ? focusFirstVisibleElement(screenBased) : focusFirstElement();
                }
                return false;
            },
            'ArrowUp': () => {
                var _a;
                if (((_a = document.activeElement) === null || _a === void 0 ? void 0 : _a.tagName) == 'BODY') {
                    const header = document.querySelector('.screenHeader.flowForm');
                    if (header) {
                        return focusFirstInForm(header);
                    }
                }
                return false;
            },
        }, false, 100);
        return () => {
            unregisterKeyHandler('flowFormGlobal');
        };
    }, []);
}
const FlowForm = ({ children, className, main, onLeaveForm }) => {
    const id = useId();
    const contextHandler = useContext(FlowFormLeaveContext);
    const handler = onLeaveForm !== null && onLeaveForm !== void 0 ? onLeaveForm : contextHandler;
    useEffect(() => {
        function mouseHandler() {
            setFocusMode('hidden');
        }
        if (handler)
            leaveFormHandlers.set(id, handler);
        document.addEventListener("mousedown", mouseHandler);
        registerAnyKeyHandler(id, () => setFocusMode('visible'));
        return () => {
            leaveFormHandlers.delete(id);
            document.removeEventListener("mousedown", mouseHandler);
            unregisterAnyKeyHandler(id);
        };
    }, [handler]);
    return _jsx("section", { "data-flowform-id": id, className: `flowForm${main ? ' mainForm' : ''} ${className !== null && className !== void 0 ? className : ''}`, children: children });
};
export default FlowForm;
